import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Pin, PinOff, Search } from 'lucide-react';
import { APPS, CATEGORIES, canSeeApp, getPinnedKeys, setPinnedKeys } from '../lib/apps';

const CHIP_STYLES = {
  core: 'bg-[hsl(var(--blue-soft))] text-primary ring-1 ring-[hsl(var(--primary)/0.18)]',
  common: 'bg-[hsl(var(--violet-soft))] text-[hsl(var(--brand-violet))] ring-1 ring-[hsl(var(--brand-violet)/0.18)]',
  utility: 'bg-muted text-foreground ring-1 ring-border',
};

const HOVER_BORDER = {
  core: 'hover:border-primary/25',
  common: 'hover:border-[hsl(var(--brand-violet)/0.3)]',
  utility: 'hover:border-foreground/20',
};

const AppTile = ({ app, pinned, onTogglePin }) => (
  <Card className={`group relative min-h-[116px] p-4 border bg-card shadow-[var(--shadow-elev-1)] hover:shadow-[var(--shadow-elev-2)] hover:bg-[hsl(var(--launcher-tile-hover))] ${HOVER_BORDER[app.category]} transition-shadow duration-150`}
    data-testid={`launcher-app-tile-${app.key}`}>
    <div className="flex items-start justify-between">
      <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${CHIP_STYLES[app.category]}`}>
        <app.icon size={19} />
      </div>
      <Button variant="ghost" size="icon" className="relative z-10 h-7 w-7 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity duration-150"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onTogglePin(app.key); }}
        aria-label={pinned ? `Unpin ${app.name}` : `Pin ${app.name}`}
        data-testid={`launcher-app-tile-${app.key}-pin`}>
        {pinned ? <PinOff size={13} /> : <Pin size={13} />}
      </Button>
    </div>
    <div className="mt-2.5 flex items-center gap-1.5 flex-wrap">
      <span className="font-heading font-semibold text-sm">{app.name}</span>
      {!app.live && (
        <Badge variant="outline" className="text-[9px] px-1 py-0 text-muted-foreground" data-testid={`launcher-app-tile-${app.key}-soon-badge`}>
          Coming Soon
        </Badge>
      )}
      {pinned && <Pin size={10} className="text-primary" aria-hidden="true" />}
    </div>
    <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{app.description}</p>
    <Link to={app.path} className="absolute inset-0 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
      aria-label={`Open ${app.name}`} data-testid={`launcher-app-tile-${app.key}-open`} />
  </Card>
);

export default function LauncherPage() {
  const auth = useAuth();
  const { user, hasPerm } = auth;
  const [query, setQuery] = useState('');
  const [pinned, setPinned] = useState(getPinnedKeys);

  const visibleApps = useMemo(
    () => APPS.filter((a) => canSeeApp(a, hasPerm, user, auth)),
    [hasPerm, user, auth]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return visibleApps;
    return visibleApps.filter((a) => a.name.toLowerCase().includes(q) || a.description.toLowerCase().includes(q));
  }, [visibleApps, query]);

  const togglePin = (key) => {
    const next = pinned.includes(key) ? pinned.filter((k) => k !== key) : [...pinned, key];
    setPinned(next);
    setPinnedKeys(next);
  };

  const pinnedApps = visibleApps.filter((a) => pinned.includes(a.key));
  const firstName = (user?.name || '').split(' ')[0];

  return (
    <div className="max-w-[1200px] mx-auto" data-testid="launcher-page">
      <div className="header-wash rounded-2xl border px-5 sm:px-8 py-6 sm:py-8">
        <h1 className="font-heading font-bold text-2xl sm:text-3xl tracking-[-0.02em]" data-testid="launcher-greeting">
          {firstName ? `Welcome back, ${firstName}` : 'Welcome to Futurists ERP'}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">One home for Sales, HRMS, projects and every team app.</p>
        <div className="relative mt-4 max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find an app…"
            className="pl-9 h-10 bg-card" data-testid="launcher-search-input" />
        </div>
      </div>

      {pinnedApps.length > 0 && !query && (
        <section className="mt-6">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2.5">Pinned</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="pinned-apps-row">
            {pinnedApps.map((a) => <AppTile key={a.key} app={a} pinned onTogglePin={togglePin} />)}
          </div>
        </section>
      )}

      {CATEGORIES.map((cat) => {
        const apps = filtered.filter((a) => a.category === cat.key);
        if (apps.length === 0) return null;
        return (
          <section key={cat.key} className="mt-6 sm:mt-8" data-testid={`launcher-section-${cat.key}`}>
            <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2.5">{cat.label}</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              {apps.map((a) => <AppTile key={a.key} app={a} pinned={pinned.includes(a.key)} onTogglePin={togglePin} />)}
            </div>
          </section>
        );
      })}

      {filtered.length === 0 && (
        <div className="mt-10 text-center text-sm text-muted-foreground" data-testid="launcher-empty-state">
          No apps match “{query}”
        </div>
      )}
    </div>
  );
}
